import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Clock,
  Download,
  Loader2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Filter,
} from 'lucide-react';

export const RevenueForecastSection = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: '',
    equipment_type: '',
    start_date: '',
    end_date: '',
  });
  const [hideZero, setHideZero] = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(true);
  const [outstandingOpen, setOutstandingOpen] = useState(true);

  // Distinct values for filter dropdowns
  const [customers, setCustomers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.equipment_type) params.append('equipment_type', filters.equipment_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const qs = params.toString();
      const res = await api.get(`/reports/revenue-forecast${qs ? `?${qs}` : ''}`);
      setData(res.data);

      // Extract distinct customers & equipment types for filter dropdowns
      const all = [...(res.data.upcoming || []), ...(res.data.outstanding || [])];
      const custMap = {};
      const eqSet = new Set();
      all.forEach(r => {
        if (r.customer_id && r.customer_name) custMap[r.customer_id] = r.customer_name;
        if (r.equipment_type) eqSet.add(r.equipment_type);
      });
      setCustomers(Object.entries(custMap).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
      setEquipmentTypes([...eqSet].sort());
    } catch (e) {
      toast.error('Failed to load revenue forecast');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const applyHideZero = (rows) => hideZero ? rows.filter(r => r.has_estimate) : rows;

  const upcoming = useMemo(() => applyHideZero(data?.upcoming || []), [data, hideZero]);
  const outstanding = useMemo(() => applyHideZero(data?.outstanding || []), [data, hideZero]);
  const summary = data?.summary || {};

  const fmtCurrency = (v) => {
    if (v == null) return '$0.00';
    return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const exportCsv = () => {
    const rows = [...upcoming.map(r => ({ ...r, _section: 'Upcoming' })), ...outstanding.map(r => ({ ...r, _section: 'Outstanding' }))];
    if (!rows.length) { toast.info('No data to export'); return; }
    const headers = ['Section', 'Job #', 'Customer', 'Site', 'Equipment Type', 'Est Hours', 'Equip Rate', 'Equip Total', 'Worker Total', 'Job Total', 'Rate Source', 'Start Date', 'Status'];
    const csvRows = rows.map(r => [
      r._section, r.job_number || '', `"${(r.customer_name || '').replace(/"/g, '""')}"`, `"${(r.site_address || '').replace(/"/g, '""')}"`,
      r.equipment_type || '', r.est_duration || 0, r.equip_rate || 0, r.equip_total || 0,
      r.worker_total || 0, r.job_total || 0, r.rate_source || '', r.start_date || '', r.status || '',
    ].join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `revenue-forecast-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const ForecastRow = ({ row }) => (
    <TableRow className="group hover:bg-muted/50">
      <TableCell className="font-mono text-xs font-medium">
        <a href={`/schedule?highlight=${row.job_id}`} className="text-[#ffd108] hover:underline flex items-center gap-1" data-testid={`forecast-job-${row.job_number}`}>
          #{row.job_number} <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      </TableCell>
      <TableCell className="text-sm max-w-[160px] truncate">{row.customer_name}</TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{row.site_address || '—'}</TableCell>
      <TableCell className="text-sm">{row.equipment_type}</TableCell>
      <TableCell className="text-right font-mono text-sm">
        {row.has_estimate ? `${row.est_duration}h` : <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500">No est.</Badge>}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">{fmtCurrency(row.equip_rate)}/{row.equip_uom?.toLowerCase() || 'hr'}</TableCell>
      <TableCell className="text-right font-mono text-sm">{row.worker_lines?.length > 0 ? fmtCurrency(row.worker_total) : '—'}</TableCell>
      <TableCell className="text-right font-mono text-sm font-semibold">{fmtCurrency(row.job_total)}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{row.rate_source || '—'}</TableCell>
    </TableRow>
  );

  const SectionTable = ({ rows, title, icon, borderClass, isOpen, onToggle, testId }) => (
    <Card className="card-industrial overflow-hidden" data-testid={testId}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
        onClick={onToggle}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-semibold text-sm">{title}</span>
          <Badge variant="secondary" className="text-xs">{rows.length} job{rows.length !== 1 ? 's' : ''}</Badge>
          <span className="text-sm font-mono font-semibold ml-2">{fmtCurrency(rows.reduce((s, r) => s + r.job_total, 0))}</span>
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {isOpen && (
        <div className={`border-t ${borderClass}`}>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No jobs in this category</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Job #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Equipment</TableHead>
                    <TableHead className="text-right">Est. Hours</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Workers</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Price List</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => <ForecastRow key={r.job_id} row={r} />)}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="forecast-loading">
        <Loader2 className="h-8 w-8 text-[#ffd108] animate-spin" />
        <p className="text-muted-foreground text-sm">Loading revenue forecast...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenue-forecast-section">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#ffd108]/20">
                <TrendingUp className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Upcoming Revenue</p>
                <p className="text-xl font-bold font-mono" data-testid="forecast-upcoming-total">{fmtCurrency(summary.upcoming_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial border-l-2 border-l-red-500/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Outstanding Revenue</p>
                <p className="text-xl font-bold font-mono" data-testid="forecast-outstanding-total">{fmtCurrency(summary.outstanding_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Combined Total</p>
                <p className="text-xl font-bold font-mono" data-testid="forecast-combined-total">{fmtCurrency(summary.combined_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Missing Estimate</p>
                <p className="text-xl font-bold font-mono" data-testid="forecast-missing-count">{summary.missing_estimate_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-industrial">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Customer</Label>
              <Select value={filters.customer_id || '_all'} onValueChange={(v) => setFilters(f => ({ ...f, customer_id: v === '_all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="forecast-filter-customer">
                  <SelectValue placeholder="All customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Equipment Type</Label>
              <Select value={filters.equipment_type || '_all'} onValueChange={(v) => setFilters(f => ({ ...f, equipment_type: v === '_all' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="forecast-filter-equipment">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All types</SelectItem>
                  {equipmentTypes.map(et => <SelectItem key={et} value={et}>{et}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From Date</Label>
              <Input type="date" className="h-8 text-xs" value={filters.start_date} onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value }))} data-testid="forecast-filter-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To Date</Label>
              <Input type="date" className="h-8 text-xs" value={filters.end_date} onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value }))} data-testid="forecast-filter-end" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={hideZero} onCheckedChange={setHideZero} id="hide-zero" data-testid="forecast-hide-zero" />
                <Label htmlFor="hide-zero" className="text-xs whitespace-nowrap cursor-pointer">Hide $0</Label>
              </div>
              <Button size="sm" variant="outline" onClick={exportCsv} className="h-8" data-testid="forecast-export-csv">
                <Download className="h-3.5 w-3.5 mr-1" /> CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outstanding Section (shown first — overdue work) */}
      <SectionTable
        rows={outstanding}
        title="Outstanding — Undocketed Past Jobs"
        icon={<AlertTriangle className="h-4 w-4 text-red-500" />}
        borderClass="border-l-2 border-l-red-500/50"
        isOpen={outstandingOpen}
        onToggle={() => setOutstandingOpen(p => !p)}
        testId="forecast-outstanding-section"
      />

      {/* Upcoming Section */}
      <SectionTable
        rows={upcoming}
        title="Upcoming — Scheduled Jobs"
        icon={<TrendingUp className="h-4 w-4 text-[#ffd108]" />}
        borderClass=""
        isOpen={upcomingOpen}
        onToggle={() => setUpcomingOpen(p => !p)}
        testId="forecast-upcoming-section"
      />

      {loading && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
    </div>
  );
};
